import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Popover from "@mui/material/Popover";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { DateTime } from "luxon";

import { PackageOfferHint } from "/imports/ui/components/booking";
import { formatPrice } from "../classHelpers";
import { WaitlistButton, LeaveWaitlistLink, waitlistState, waitlistCaption } from "./WaitlistAction";

// Desktop week-grid class popover — full class detail + Join, anchored to the
// clicked chip.
function ClassOptionsPopover({ cls: state }) {
  const {
    currency,
    optionsAnchor,
    optionsClass,
    closeOptionsPopover,
    handleJoin,
    handleJoinWaitlist,
    handleLeaveWaitlist,
    handleOpenDetail,
    packageOfferFor,
    handleViewPackage
  } = state;

  const cls = optionsClass;
  const isFull = cls ? cls.spotsAvailable === 0 : false;
  const { eligible, reason, needsAssessment } = cls?.eligibility || {};
  const canJoin = cls ? !isFull && eligible !== false && !cls.isJoined : false;
  const waitlist = cls ? waitlistState(cls) : { show: false };
  const packageOffer = cls ? packageOfferFor(cls) : null;

  return (
    <Popover
      open={!!optionsAnchor}
      anchorEl={optionsAnchor}
      onClose={closeOptionsPopover}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{ sx: { p: 2, borderRadius: 2.5, width: "17.5rem", boxShadow: "0 8px 30px rgba(26,26,26,0.16)" } }}
    >
      {cls && (
        <Box>
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, lineHeight: 1.2 }}>{cls.name}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {DateTime.fromISO(cls.date).toFormat("ccc MMM d")} · {cls.time} · {cls.duration} min
          </Typography>
          {cls.trainer && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              with {cls.trainer.name}
            </Typography>
          )}
          {(cls.facility || cls.location) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25, minWidth: 0 }}>
              <PlaceOutlinedIcon sx={{ fontSize: "1.05rem", color: "primary.main", flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                {cls.facility?.name}
                {cls.facility && cls.location ? " · " : ""}
                {cls.location?.name ? (
                  <Box component="span" sx={{ fontWeight: 700, color: "text.primary", fontSize: "0.9375rem" }}>
                    {cls.location.name}
                  </Box>
                ) : null}
              </Typography>
            </Box>
          )}
          {cls.levelRequiredNames ? (
            <Box mt={0.75}>
              <Chip label={cls.levelRequiredNames} size="small" variant="outlined" color="warning" sx={{ fontSize: "0.65rem", height: "1.25rem" }} />
            </Box>
          ) : null}
          {!canJoin && reason ? (
            <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.75 }}>
              {reason}
            </Typography>
          ) : null}
          {packageOffer ? (
            <Box sx={{ mt: 0.75 }}>
              <PackageOfferHint
                offer={packageOffer}
                currency={currency}
                compact
                onClick={() => {
                  closeOptionsPopover();
                  handleViewPackage(packageOffer, cls);
                }}
              />
            </Box>
          ) : null}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1.5 }}>
            <Box>
              {cls.price != null && (
                <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
                  {formatPrice(cls.price, currency)}
                </Typography>
              )}
              <Typography
                variant="caption"
                color={cls.isJoined ? "success.main" : waitlist.listed ? "primary.main" : isFull ? "error.main" : "text.secondary"}
              >
                {cls.isJoined
                  ? "You're in"
                  : waitlistCaption(cls) || (isFull ? "Full" : `${cls.spotsAvailable} spot${cls.spotsAvailable !== 1 ? "s" : ""} left`)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 0.75 }}>
              <Button
                size="small"
                onClick={() => {
                  handleOpenDetail(cls);
                  closeOptionsPopover();
                }}
                sx={{ minWidth: 0, textTransform: "none", fontWeight: 600 }}
              >
                Details
              </Button>
              <LeaveWaitlistLink
                cls={cls}
                onLeave={(c) => {
                  handleLeaveWaitlist(c);
                  closeOptionsPopover();
                }}
              />
              {waitlist.show ? (
                <WaitlistButton
                  cls={cls}
                  onJoin={(c) => {
                    handleJoinWaitlist(c);
                    closeOptionsPopover();
                  }}
                  onLeave={(c) => {
                    handleLeaveWaitlist(c);
                    closeOptionsPopover();
                  }}
                  icons={false}
                  sx={{ minWidth: "4.5rem", whiteSpace: "nowrap" }}
                />
              ) : (
                <Button
                  size="small"
                  variant={cls.isJoined ? "outlined" : canJoin ? "contained" : "outlined"}
                  disabled={!canJoin}
                  onClick={() => {
                    handleJoin(cls);
                    closeOptionsPopover();
                  }}
                  sx={{ minWidth: "4.5rem", ...(cls.isJoined && { borderColor: "success.main", color: "success.main" }) }}
                >
                  {cls.isJoined ? "Joined" : isFull ? "Full" : needsAssessment ? "Assess" : cls.wasJoined ? "Re-join" : "Join"}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Popover>
  );
}

export default ClassOptionsPopover;
